import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useHasRole } from "@/components/RoleGate";
import { COMMON_CURRENCIES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SUPPORTED_LANGUAGES } from "@/i18n";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  tax_id: "",
  is_business: true,
  personal_number: "",
  preferred_language: "en",
  default_payment_terms_days: 30,
  currency: "SEK",
  notes: "",
  is_active: true,
  address: { street: "", city: "", postal_code: "", country: "" },
};

function CustomerDialog({ mode, open, onOpenChange, initial, defaults, onSubmit, submitting }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setForm({
        name: initial.name || "",
        email: initial.email || "",
        phone: initial.phone || "",
        tax_id: initial.tax_id || "",
        is_business: initial.is_business !== false,
        personal_number: initial.personal_number || "",
        preferred_language: initial.preferred_language || defaults.language,
        default_payment_terms_days: initial.default_payment_terms_days ?? 30,
        currency: initial.currency || defaults.currency,
        notes: initial.notes || "",
        is_active: !!initial.is_active,
        address: {
          street: initial.address?.street || "",
          city: initial.address?.city || "",
          postal_code: initial.address?.postal_code || "",
          country: initial.address?.country || "",
        },
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        preferred_language: defaults.language,
        currency: defaults.currency,
      });
    }
  }, [open, mode, initial, defaults.currency, defaults.language]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }
  function setAddr(field, value) {
    setForm((f) => ({ ...f, address: { ...f.address, [field]: value } }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const payload = {
      name: form.name,
      email: form.email || null,
      phone: form.phone || null,
      tax_id: form.is_business ? (form.tax_id || null) : null,
      is_business: form.is_business,
      personal_number: !form.is_business ? (form.personal_number || null) : null,
      preferred_language: form.preferred_language,
      default_payment_terms_days: Number(form.default_payment_terms_days),
      currency: form.currency,
      notes: form.notes || null,
      address:
        form.address.street || form.address.city || form.address.postal_code || form.address.country
          ? form.address
          : null,
    };
    if (mode === "edit") payload.is_active = form.is_active;
    await onSubmit(payload);
  }

  const titleKey = mode === "edit" ? "customers.edit.title" : "customers.create.title";
  const descKey = mode === "edit" ? "customers.edit.description" : "customers.create.description";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        data-testid={`customer-dialog-${mode}`}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight">
            {t(titleKey)}
          </DialogTitle>
          <DialogDescription>{t(descKey)}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="cust-name">{t("customers.form.name")}</Label>
              <Input
                id="cust-name"
                required
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                data-testid="customer-form-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cust-email">{t("customers.form.email")}</Label>
              <Input
                id="cust-email"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                data-testid="customer-form-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cust-phone">{t("customers.form.phone")}</Label>
              <Input
                id="cust-phone"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                data-testid="customer-form-phone"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label>{t("customers.form.customerType")}</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => set("is_business", true)}
                  data-testid="customer-form-type-business"
                  className={`flex-1 rounded-md border px-3 py-2 text-sm ${form.is_business ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700"}`}
                >
                  {t("customers.form.typeBusiness")}
                </button>
                <button
                  type="button"
                  onClick={() => set("is_business", false)}
                  data-testid="customer-form-type-individual"
                  className={`flex-1 rounded-md border px-3 py-2 text-sm ${!form.is_business ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700"}`}
                >
                  {t("customers.form.typeIndividual")}
                </button>
              </div>
            </div>

            {form.is_business ? (
              <div className="space-y-2">
                <Label htmlFor="cust-taxid">{t("customers.form.taxId")}</Label>
                <Input
                  id="cust-taxid"
                  value={form.tax_id}
                  onChange={(e) => set("tax_id", e.target.value)}
                  data-testid="customer-form-taxid"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="cust-pn">{t("customers.form.personalNumber")}</Label>
                <Input
                  id="cust-pn"
                  placeholder="YYYYMMDD-XXXX"
                  value={form.personal_number}
                  onChange={(e) => set("personal_number", e.target.value)}
                  data-testid="customer-form-personal-number"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="cust-terms">{t("customers.form.paymentTerms")}</Label>
              <Input
                id="cust-terms"
                type="number"
                min={0}
                max={365}
                value={form.default_payment_terms_days}
                onChange={(e) => set("default_payment_terms_days", e.target.value)}
                data-testid="customer-form-terms"
              />
            </div>

            <div className="space-y-2">
              <Label>{t("customers.form.language")}</Label>
              <Select
                value={form.preferred_language}
                onValueChange={(v) => set("preferred_language", v)}
              >
                <SelectTrigger data-testid="customer-form-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((lng) => (
                    <SelectItem key={lng} value={lng}>
                      {t(`languages.${lng}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("customers.form.currency")}</Label>
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger data-testid="customer-form-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <fieldset className="space-y-3 rounded-md border border-slate-200 p-4">
            <legend className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("customers.form.address")}
            </legend>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>{t("customers.form.street")}</Label>
                <Input
                  value={form.address.street}
                  onChange={(e) => setAddr("street", e.target.value)}
                  data-testid="customer-form-street"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("customers.form.city")}</Label>
                <Input
                  value={form.address.city}
                  onChange={(e) => setAddr("city", e.target.value)}
                  data-testid="customer-form-city"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("customers.form.postalCode")}</Label>
                <Input
                  value={form.address.postal_code}
                  onChange={(e) => setAddr("postal_code", e.target.value)}
                  data-testid="customer-form-postal"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>{t("customers.form.country")}</Label>
                <Input
                  placeholder="SE"
                  value={form.address.country}
                  onChange={(e) => setAddr("country", e.target.value)}
                  data-testid="customer-form-country"
                />
              </div>
            </div>
          </fieldset>

          <div className="space-y-2">
            <Label htmlFor="cust-notes">{t("customers.form.notes")}</Label>
            <Textarea
              id="cust-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              data-testid="customer-form-notes"
            />
          </div>

          {mode === "edit" && (
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <Label htmlFor="cust-active" className="cursor-pointer">
                {t("customers.form.active")}
              </Label>
              <Switch
                id="cust-active"
                checked={form.is_active}
                onCheckedChange={(v) => set("is_active", v)}
                data-testid="customer-form-active"
              />
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="customer-form-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="customer-form-submit"
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {mode === "edit" ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const { t } = useTranslation();
  const { company } = useAuth();
  const canCreate = useHasRole("admin", "accountant", "user");
  const canEdit = useHasRole("admin", "accountant");
  const canDelete = useHasRole("admin");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [dialogState, setDialogState] = useState({ open: false, mode: "create", initial: null });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const defaults = useMemo(
    () => ({
      currency: company?.default_currency || "SEK",
      language: company?.default_language || "en",
    }),
    [company]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (!showInactive) params.set("is_active", "true");
      params.set("page", "1");
      params.set("page_size", "100");
      const { data } = await api.get(`/customers?${params.toString()}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(formatApiError(err, t("customers.toasts.error")));
    } finally {
      setLoading(false);
    }
  }, [search, showInactive, t]);

  useEffect(() => {
    const handle = setTimeout(load, 250);
    return () => clearTimeout(handle);
  }, [load]);

  function openCreate() {
    setDialogState({ open: true, mode: "create", initial: null });
  }
  function openEdit(c) {
    setDialogState({ open: true, mode: "edit", initial: c });
  }

  async function handleSubmit(payload) {
    setSubmitting(true);
    try {
      if (dialogState.mode === "create") {
        await api.post("/customers", payload);
        toast.success(t("customers.toasts.created"));
      } else {
        await api.patch(`/customers/${dialogState.initial.id}`, payload);
        toast.success(t("customers.toasts.updated"));
      }
      setDialogState({ open: false, mode: "create", initial: null });
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("customers.toasts.error")));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setSubmitting(true);
    const deletedId = confirmDelete.id;
    try {
      await api.delete(`/customers/${deletedId}`);
      toast.success(t("customers.toasts.deleted"));
      setConfirmDelete(null);
      // Optimistic update: drop from current list immediately so the UI
      // reflects the change before the refetch finishes.
      setItems((curr) => curr.filter((x) => x.id !== deletedId));
      setTotal((t0) => Math.max(0, t0 - 1));
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("customers.toasts.error")));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-8" data-testid="customers-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <h1 className="font-heading text-4xl tracking-tight text-slate-900">
            {t("customers.title")}
          </h1>
          <p className="max-w-xl text-sm leading-relaxed text-slate-500">
            {t("customers.subtitle", { company: company?.name || "—" })}
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={openCreate}
            data-testid="customers-add-button"
            className="gap-2 bg-slate-900 text-white hover:bg-slate-800"
          >
            <Plus className="h-4 w-4" />
            {t("customers.addCustomer")}
          </Button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t("common.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ps-9"
            data-testid="customers-search"
          />
        </div>
        <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
          <Switch
            checked={showInactive}
            onCheckedChange={setShowInactive}
            data-testid="customers-show-inactive"
          />
          <span>{t("common.showInactive")}</span>
        </label>
        <div className="ms-auto text-xs text-slate-500" data-testid="customers-total">
          {t("common.totalCount", { count: total })}
        </div>
      </div>

      <div className="mobile-cards overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("customers.table.name")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("customers.table.email")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("customers.table.country")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("customers.table.currency")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("customers.table.language")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("customers.table.terms")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("customers.table.status")}
              </TableHead>
              <TableHead className="text-end text-xs uppercase tracking-wide text-slate-500">
                {t("common.actions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} className="py-8 text-center text-sm text-slate-500">
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-8 text-center text-sm text-slate-500">
                  {t("customers.empty")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              items.map((c) => (
                <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                  <TableCell className="font-medium text-slate-900" data-label={t("customers.table.name")}>{c.name}</TableCell>
                  <TableCell className="text-slate-600" data-label={t("customers.table.email")}>{c.email || "—"}</TableCell>
                  <TableCell className="text-slate-600 uppercase text-xs tracking-wide" data-label={t("customers.table.country")}>
                    {c.address?.country || "—"}
                  </TableCell>
                  <TableCell className="text-slate-700 font-medium" data-label={t("customers.table.currency")}>{c.currency}</TableCell>
                  <TableCell className="text-slate-600 uppercase text-xs tracking-wide" data-label={t("customers.table.language")}>
                    {c.preferred_language}
                  </TableCell>
                  <TableCell className="text-slate-600" data-label={t("customers.table.terms")}>
                    {t("customers.table.daysValue", { count: c.default_payment_terms_days })}
                  </TableCell>
                  <TableCell data-label={t("customers.table.status")}>
                    {c.is_active ? (
                      <span className="inline-flex items-center gap-2 text-xs text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        {t("common.active")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-xs text-slate-500">
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                        {t("common.inactive")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-end" data-label={t("common.actions")}>
                    <div className="inline-flex items-center gap-1">
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(c)}
                          data-testid={`customer-edit-${c.id}`}
                          className="gap-1 text-slate-700"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          {t("common.edit")}
                        </Button>
                      )}
                      {canDelete && c.is_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmDelete(c)}
                          data-testid={`customer-delete-${c.id}`}
                          className="gap-1 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t("common.delete")}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <CustomerDialog
        mode={dialogState.mode}
        open={dialogState.open}
        onOpenChange={(o) =>
          setDialogState((s) => ({ ...s, open: o, initial: o ? s.initial : null }))
        }
        initial={dialogState.initial}
        defaults={defaults}
        onSubmit={handleSubmit}
        submitting={submitting}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="customer-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("customers.delete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("customers.delete.description", {
                name: confirmDelete?.name || "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="customer-delete-cancel">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={submitting}
              data-testid="customer-delete-confirm-button"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {t("customers.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
